// Print-margin lint: detect CSS that makes on-screen margins disappear in print,
// and a mechanical fix that restores them. No AI/LLM involved — pure string/CSS
// analysis so it is instant, free, and deterministic.

function stripComments(css) {
  return css.replace(/\/\*[\s\S]*?\*\//g, "");
}

function extractStyleTexts(html) {
  const texts = [];
  const re = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
  let m;
  while ((m = re.exec(html))) texts.push(m[1]);
  return texts;
}

// Index of the matching '}' for the '{' at openIdx (opening brace counted as depth 1).
function findMatchingBrace(str, openIdx) {
  let depth = 0;
  for (let i = openIdx; i < str.length; i++) {
    if (str[i] === "{") depth++;
    else if (str[i] === "}") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

// Parse top-level (non-@media) rules into { selector, body }.
function parseRules(css) {
  const rules = [];
  let i = 0;
  while (i < css.length) {
    const brace = css.indexOf("{", i);
    if (brace === -1) break;
    const sel = css.slice(i, brace).trim();
    const close = findMatchingBrace(css, brace);
    if (close === -1) break;
    const body = css.slice(brace + 1, close);
    if (sel && !sel.startsWith("@")) rules.push({ selector: sel, body });
    i = close + 1;
  }
  return rules;
}

function declValue(body, prop) {
  const re = new RegExp(prop + "\\s*:\\s*([^;{}]+)", "i");
  const m = body.match(re);
  return m ? m[1].trim() : null;
}

const UNIT = "(?:px|mm|cm|em|rem|%|pt|vh|vw|ex|ch|in)?";

function isZero(value) {
  if (!value) return false;
  const v = value.replace(/\s*!important/i, "").trim();
  return /^0(?:px|mm|cm|em|rem|%|pt|vh|vw|ex|ch|in)?(?:\s+0(?:px|mm|cm|em|rem|%|pt|vh|vw|ex|ch|in)?)*$/.test(v);
}

// Map of selector -> { hasPadding, hasMargin } based on screen (non-print) rules.
function buildScreenMap(styleTexts) {
  const map = {};
  for (const css of styleTexts) {
    const clean = stripComments(css);
    for (const r of parseRules(clean)) {
      const selectors = r.selector.split(",").map((s) => s.trim()).filter(Boolean);
      const pad = declValue(r.body, "padding");
      const mar = declValue(r.body, "margin");
      for (const s of selectors) {
        const entry = map[s] || (map[s] = { hasPadding: false, hasMargin: false });
        if (pad && !isZero(pad)) entry.hasPadding = true;
        if (mar && !isZero(mar)) entry.hasMargin = true;
      }
    }
  }
  return map;
}

function removeZeroDecl(body, prop) {
  const re = new RegExp(
    prop + "\\s*:\\s*0" + UNIT + "(?:\\s+0" + UNIT + ")*\\s*(?:!important)?\\s*;?",
    "gi"
  );
  return body
    .replace(re, "")
    .replace(/;(\s*;)+/g, ";")
    .replace(/\{\s*;/g, "{")
    .replace(/;\s*\}/g, "}");
}

function eachMediaPrintRule(css, cb) {
  let idx = 0;
  while (true) {
    const at = css.indexOf("@media", idx);
    if (at === -1) break;
    const qm = css.slice(at).match(/^@media\s+([^{]+)/);
    if (!qm || !/^print\b/i.test(qm[1].trim())) {
      idx = at + 6;
      continue;
    }
    const brace = css.indexOf("{", at);
    if (brace === -1) break;
    const close = findMatchingBrace(css, brace);
    if (close === -1) break;
    cb(css.slice(brace + 1, close), brace, close);
    idx = close + 1;
  }
}

/**
 * Detect print rules that zero out margins/padding that exist on screen.
 * Returns [{ selector, kind: 'padding' | 'margin' }].
 */
export function lintPrintIssues(code) {
  const issues = [];
  if (typeof code !== "string" || !code) return issues;
  const styleTexts = extractStyleTexts(code);
  const screenMap = buildScreenMap(styleTexts);

  const seen = new Set();
  for (const css of styleTexts) {
    eachMediaPrintRule(css, (inner) => {
      let j = 0;
      while (true) {
        const b2 = inner.indexOf("{", j);
        if (b2 === -1) break;
        const sel = inner.slice(j, b2).trim();
        const c2 = findMatchingBrace(inner, b2);
        if (c2 === -1) break;
        const body = inner.slice(b2 + 1, c2);
        const selectors = sel.split(",").map((s) => s.trim()).filter(Boolean);
        const hasScreenPad = selectors.some((s) => screenMap[s] && screenMap[s].hasPadding);
        const hasScreenMar = selectors.some((s) => screenMap[s] && screenMap[s].hasMargin);
        const padVal = declValue(body, "padding");
        const marVal = declValue(body, "margin");
        if (hasScreenPad && padVal && isZero(padVal)) {
          const key = sel + "|padding";
          if (!seen.has(key)) {
            seen.add(key);
            issues.push({ selector: sel, kind: "padding" });
          }
        }
        if (hasScreenMar && marVal && isZero(marVal)) {
          const key = sel + "|margin";
          if (!seen.has(key)) {
            seen.add(key);
            issues.push({ selector: sel, kind: "margin" });
          }
        }
        j = c2 + 1;
      }
    });
  }
  return issues;
}

/**
 * Mechanically fix: inside `@media print` blocks, remove `padding: 0` / `margin: 0`
 * declarations for selectors that have real margins on screen, so the preview's
 * margins survive into print. Returns the (possibly unchanged) code.
 */
export function fixPrintIssues(code) {
  if (typeof code !== "string" || !code) return code;
  const screenMap = buildScreenMap(extractStyleTexts(code));

  return code.replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (full, styleText) => {
    let fixed = styleText;
    let changed = false;

    let idx = 0;
    while (true) {
      const at = fixed.indexOf("@media", idx);
      if (at === -1) break;
      const qm = fixed.slice(at).match(/^@media\s+([^{]+)/);
      if (!qm || !/^print\b/i.test(qm[1].trim())) {
        idx = at + 6;
        continue;
      }
      const brace = fixed.indexOf("{", at);
      if (brace === -1) break;
      const close = findMatchingBrace(fixed, brace);
      if (close === -1) break;
      const inner = fixed.slice(brace + 1, close);

      let innerFixed = inner;
      let j = 0;
      while (true) {
        const b2 = innerFixed.indexOf("{", j);
        if (b2 === -1) break;
        const sel = innerFixed.slice(j, b2).trim();
        const c2 = findMatchingBrace(innerFixed, b2);
        if (c2 === -1) break;
        const body = innerFixed.slice(b2 + 1, c2);
        const selectors = sel.split(",").map((s) => s.trim()).filter(Boolean);
        const hasScreenPad = selectors.some((s) => screenMap[s] && screenMap[s].hasPadding);
        const hasScreenMar = selectors.some((s) => screenMap[s] && screenMap[s].hasMargin);
        const padVal = declValue(body, "padding");
        const marVal = declValue(body, "margin");
        let newBody = body;
        if (hasScreenPad && padVal && isZero(padVal)) newBody = removeZeroDecl(newBody, "padding");
        if (hasScreenMar && marVal && isZero(marVal)) newBody = removeZeroDecl(newBody, "margin");
        if (newBody !== body) {
          innerFixed = innerFixed.slice(0, b2 + 1) + newBody + innerFixed.slice(c2);
          changed = true;
        }
        j = c2 + 1;
      }

      if (changed) {
        fixed = fixed.slice(0, brace + 1) + innerFixed + fixed.slice(close);
      }
      idx = close + 1;
    }

    if (fixed === styleText) return full;
    return full.replace(styleText, fixed);
  });
}

// Detect scroll/fixed layouts that clip content on print (e.g. height:100vh +
// overflow:scroll shows only the first viewport). Returns [{ selector, kind:'overflow' }].
export function lintPrintOverflow(code) {
  if (typeof code !== "string" || !code) return [];
  if (/frontplay-print-overflow-fix/i.test(code)) return []; // already handled
  const issues = [];
  for (const css of extractStyleTexts(code)) {
    const clean = stripComments(css);
    for (const r of parseRules(clean)) {
      const selectors = r.selector.split(",").map((s) => s.trim()).filter(Boolean);
      if (!selectors.some((s) => s === "body" || s === "html")) continue;
      const overflow = declValue(r.body, "overflow") || declValue(r.body, "overflow-y");
      const height = declValue(r.body, "height");
      if (!overflow || !height) continue;
      const ov = overflow.replace(/!important/i, "").trim();
      const ht = height.replace(/!important/i, "").trim();
      if (/^(hidden|scroll|auto)$/i.test(ov) && /(100vh|100%)$/i.test(ht)) {
        issues.push({ selector: r.selector, kind: "overflow" });
      }
    }
  }
  return issues;
}

// Mechanically fix: inject a print-reset <style> so all content prints.
export function fixPrintOverflow(code) {
  if (typeof code !== "string" || !code) return code;
  if (/frontplay-print-overflow-fix/i.test(code)) return code; // idempotent
  const fix = `<style id="frontplay-print-overflow-fix">\n@media print {\n  html, body { height: auto !important; overflow: visible !important; }\n  body * { overflow: visible !important; max-height: none !important; }\n  * { position: static !important; }\n}\n</style>`;
  if (/<\/head>/i.test(code)) return code.replace(/<\/head>/i, (m) => `${fix}\n${m}`);
  if (/<head[^>]*>/i.test(code)) return code.replace(/<head[^>]*>/i, (m) => `${m}\n${fix}`);
  if (/<body[^>]*>/i.test(code)) return code.replace(/<body[^>]*>/i, (m) => `${m}\n${fix}`);
  if (/<\/body>/i.test(code)) return code.replace(/<\/body>/i, (m) => `${fix}\n${m}`);
  return code + fix;
}
